import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe } from 'lucide-react';

export default function LanguageSwitcher({ variant = 'default' }) {
  const { i18n } = useTranslation();
  
  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    // Update document direction
    document.documentElement.dir = langCode === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = langCode;
    // Add/remove Arabic font class
    if (langCode === 'ar') {
      document.body.classList.add('font-arabic');
    } else {
      document.body.classList.remove('font-arabic');
    }
  };

  // Set initial direction on mount
  React.useEffect(() => {
    const currentLangCode = i18n.language;
    document.documentElement.dir = currentLangCode === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLangCode;
    if (currentLangCode === 'ar') {
      document.body.classList.add('font-arabic');
    }
  }, [i18n.language]);

  if (variant === 'minimal') {
    return (
      <button
        onClick={() => changeLanguage(i18n.language === 'en' ? 'ar' : 'en')}
        className="flex items-center gap-1 px-2 py-1 text-sm text-slate-600 hover:text-slate-900 transition-colors"
      >
        <span>{currentLang.flag}</span>
        <span className="hidden sm:inline">{currentLang.code.toUpperCase()}</span>
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="flex items-center gap-2">
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{currentLang.code === 'en' ? 'GB' : 'SA'} {currentLang.name}</span>
          <span className="sm:hidden">{currentLang.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 bg-white border border-slate-200 shadow-lg z-[100]">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`flex items-center justify-between gap-3 cursor-pointer px-4 py-2.5 text-slate-700 hover:bg-slate-50 ${
              i18n.language === lang.code ? 'bg-slate-50' : ''
            }`}
          >
            <div className="flex items-center gap-2">
              <span>{lang.flag}</span>
              <span className="font-medium">{lang.name}</span>
            </div>
            {i18n.language === lang.code && (
              <span className="text-blue-600">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
